// doctor-frontend/src/pages/admin/UserManagement.jsx
// Tenant-scoped CMFM user administration.
//
// Preserved behaviour:
// - Admin can create, list, edit, activate/deactivate and soft-disable users.
// - Faculty and Admin remain available assignable roles.
// - Existing profile backfill and profile navigation are retained.
// - Tenant-safe operation is delegated to the shared HTTP service.
// - Legacy /api/admin/users endpoints remain supported as narrow fallbacks.
//
// July 2026 alignment:
// - Uses the canonical /api/users and /api/users/register endpoints first.
// - Supports all current CMFM/CHC staff roles without exposing Citizen creation.
// - Uses cryptographically secure temporary-password generation when available.
// - Handles first-login password flags, active status and alternate identifiers.
// - Prevents Create User from being treated as an edit/reset of an existing account.
// - Removes random row keys, unsafe token scanning and false-success messages.

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useNavigate } from 'react-router-dom';
import http from '../../services/http';

const ROLE_OPTIONS = [
  { value: 'intern', label: 'Intern' },
  { value: 'junior resident', label: 'Junior Resident' },
  { value: 'senior resident', label: 'Senior Resident' },
  { value: 'faculty', label: 'Faculty' },
  { value: 'hod', label: 'HOD' },
  { value: 'admin', label: 'Admin' },
  { value: 'deo', label: 'DEO' },
  { value: 'medical student', label: 'Medical Student' },
  { value: 'medical officer', label: 'Medical Officer' },
  { value: 'lab technician', label: 'Lab Technician' },
  { value: 'radiographer', label: 'Radiographer' },
  { value: 'nursing officer', label: 'Nursing Officer' },
  { value: 'pharmacist', label: 'Pharmacist' },
];

const CENTRES = [
  'CRHA Keru',
  'CRHA Dhawa',
  'CRHA Dhundhara',
  'UHTC Pratap Nagar',
];

const INITIAL_CREATE_FORM = {
  name: '',
  email: '',
  mobile: '',
  username: '',
  srId: '',
  role: 'junior resident',
  centre: '',
  password: '',
  isActive: true,
  mustChangePassword: true,
};

const INITIAL_EDIT_FORM = {
  name: '',
  email: '',
  mobile: '',
  username: '',
  srId: '',
  staffId: '',
  employeeId: '',
  role: '',
  centre: '',
  isActive: true,
};

function stringValue(value) {
  if (value === undefined || value === null) return '';
  const normalized = String(value).trim();
  return normalized === 'undefined' || normalized === 'null' ? '' : normalized;
}

function safeJsonParse(value) {
  try {
    return JSON.parse(value || 'null');
  } catch {
    return null;
  }
}

function getTenantSlug() {
  try {
    const match = (window.location.pathname || '').match(/^\/t\/([^/]+)/i);
    if (match?.[1]) return decodeURIComponent(match[1]);
    return localStorage.getItem('tenantSlug') || '';
  } catch {
    return '';
  }
}

function tenantUrl(path) {
  const slug = getTenantSlug();
  const value = String(path || '');
  const clean = value.startsWith('/') ? value : `/${value}`;

  if (!slug) return clean;
  if (clean.startsWith('/t/')) return clean;
  return `/t/${encodeURIComponent(slug)}${clean}`.replace(/\/{2,}/g, '/');
}

function getLoggedInUser() {
  try {
    const candidates = [
      localStorage.getItem('loggedInUser'),
      localStorage.getItem('cmfm_user'),
      localStorage.getItem('cmfmUser'),
      sessionStorage.getItem('loggedInUser'),
      sessionStorage.getItem('cmfm_user'),
      sessionStorage.getItem('cmfmUser'),
    ];

    for (const raw of candidates) {
      const parsed = safeJsonParse(raw);
      const user = parsed?.user || parsed;
      if (user && typeof user === 'object') return user;
    }
  } catch {}

  return {};
}

function normalizeRole(value) {
  const role = stringValue(value).toLowerCase();
  if (!role) return '';

  if (['jr', 'junior', 'juniorresident', 'junior resident'].includes(role)) {
    return 'junior resident';
  }
  if (['sr', 'senior', 'seniorresident', 'senior resident'].includes(role)) {
    return 'senior resident';
  }
  if (['prof', 'professor', 'faculty'].includes(role)) return 'faculty';
  if (['administrator'].includes(role) || role.includes('admin')) return 'admin';
  if (['data entry operator', 'data-entry-operator'].includes(role)) return 'deo';
  if (['medical-officer', 'mo'].includes(role)) return 'medical officer';
  if (['lab tech', 'laboratory', 'laboratory technician'].includes(role)) {
    return 'lab technician';
  }
  if (['radiology', 'radiologist'].includes(role)) return 'radiographer';
  if (['nurse', 'nursing'].includes(role)) return 'nursing officer';
  if (['pharmacy', 'pharmacology'].includes(role)) return 'pharmacist';
  if (['head of department'].includes(role)) return 'hod';
  if (['student', 'ug', 'undergraduate', 'mbbs'].includes(role)) {
    return 'medical student';
  }

  return role;
}

function normalizeCentre(value) {
  const original = stringValue(value);
  if (!original) return '';

  const normalized = original
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (normalized.includes('keru')) return 'CRHA Keru';
  if (normalized.includes('dhawa')) return 'CRHA Dhawa';
  if (normalized.includes('pratap')) return 'UHTC Pratap Nagar';
  if (
    ['dundhara', 'dhundhara', 'dundara', 'dhundara'].some((part) =>
      normalized.includes(part)
    )
  ) {
    return 'CRHA Dhundhara';
  }

  return original;
}

function roleChipLabel(role) {
  const normalized = normalizeRole(role);
  const found = ROLE_OPTIONS.find((option) => option.value === normalized);
  return found?.label || stringValue(role) || '—';
}

function roleChipColor(role) {
  const normalized = normalizeRole(role);
  if (normalized === 'admin') return 'error';
  if (normalized === 'hod') return 'error';
  if (normalized === 'faculty') return 'secondary';
  if (normalized === 'senior resident') return 'warning';
  if (normalized === 'junior resident') return 'info';
  if (normalized === 'medical officer') return 'primary';
  if (normalized === 'intern') return 'success';
  return 'default';
}

function userIsActive(user) {
  if (user?.isActive === false) return false;

  const status = stringValue(user?.status).toLowerCase();
  if (['inactive', 'disabled', 'false', '0'].includes(status)) return false;
  return true;
}

function statusOf(errorOrResponse) {
  return Number(
    errorOrResponse?.status ||
      errorOrResponse?.response?.status ||
      errorOrResponse?.data?.status ||
      0
  );
}

function payloadOf(response) {
  return response && Object.prototype.hasOwnProperty.call(response, 'data')
    ? response.data
    : response;
}

function getErrorMessage(error, fallback) {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    fallback
  );
}

function normalizeUsers(payload) {
  const source = payloadOf(payload) || {};
  const candidates = [
    source?.items,
    source?.users,
    source?.data?.items,
    source?.data?.users,
    source?.data,
    source?.result?.items,
    source?.result?.users,
    source,
  ];

  return candidates.find(Array.isArray) || [];
}

async function requestCandidate(candidate) {
  const { method = 'GET', url, data, options } = candidate;
  const verb = String(method).toLowerCase();

  if (verb === 'get') return http.get(url, options);
  if (verb === 'post') return http.post(url, data, options);
  if (verb === 'put') return http.put(url, data, options);
  if (verb === 'patch') return http.patch(url, data, options);
  if (verb === 'delete') return http.delete(url, options);

  throw new Error(`Unsupported HTTP method: ${method}`);
}

async function requestWithFallback(candidates) {
  let lastError = null;

  for (let index = 0; index < candidates.length; index += 1) {
    try {
      return await requestCandidate(candidates[index]);
    } catch (error) {
      lastError = error;
      const status = statusOf(error);
      const hasNext = index < candidates.length - 1;

      if (hasNext && [404, 405].includes(status)) continue;
      throw error;
    }
  }

  throw lastError || new Error('API endpoint is unavailable.');
}

function randomIndex(maximum) {
  if (maximum <= 1) return 0;

  try {
    const cryptoObject = typeof window !== 'undefined' ? window.crypto : null;
    if (cryptoObject?.getRandomValues) {
      const values = new Uint32Array(1);
      cryptoObject.getRandomValues(values);
      return values[0] % maximum;
    }
  } catch {}

  return Math.floor(Math.random() * maximum);
}

function shuffleCharacters(characters) {
  const output = [...characters];
  for (let index = output.length - 1; index > 0; index -= 1) {
    const swapIndex = randomIndex(index + 1);
    [output[index], output[swapIndex]] = [output[swapIndex], output[index]];
  }
  return output.join('');
}

function generateTemporaryPassword(length = 14) {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
  const lower = 'abcdefghijkmnopqrstuvwxyz';
  const numbers = '23456789';
  const symbols = '@#$%';
  const alphabet = `${upper}${lower}${numbers}${symbols}`;
  const safeLength = Math.max(10, Math.min(Number(length) || 14, 32));

  const required = [
    upper[randomIndex(upper.length)],
    lower[randomIndex(lower.length)],
    numbers[randomIndex(numbers.length)],
    symbols[randomIndex(symbols.length)],
  ];

  while (required.length < safeLength) {
    required.push(alphabet[randomIndex(alphabet.length)]);
  }

  return shuffleCharacters(required);
}

function isValidEmail(value) {
  const email = stringValue(value).toLowerCase();
  return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidMobile(value) {
  const mobile = stringValue(value).replace(/[\s()-]/g, '');
  return !mobile || /^\+?\d{6,15}$/.test(mobile);
}

function findExistingCreateIdentity(users, email, mobile) {
  const normalizedEmail = stringValue(email).toLowerCase();
  const normalizedMobile = stringValue(mobile).replace(/[\s()-]/g, '');

  return (Array.isArray(users) ? users : []).find((user) => {
    const userEmail = stringValue(user?.email).toLowerCase();
    const userMobile = stringValue(user?.mobile).replace(/[\s()-]/g, '');

    return Boolean(
      (normalizedEmail && userEmail && normalizedEmail === userEmail) ||
        (normalizedMobile && userMobile && normalizedMobile === userMobile)
    );
  });
}

function stableUserKey(user, index) {
  return (
    stringValue(user?._id || user?.id) ||
    stringValue(user?.email) ||
    stringValue(user?.mobile) ||
    `${stringValue(user?.name || user?.fullName) || 'user'}-${index}`
  );
}

export default function UserManagement() {
  const navigate = useNavigate();
  const currentUser = useMemo(getLoggedInUser, []);
  const currentRole = normalizeRole(currentUser?.role || currentUser?.userType);
  const canManageUsers = ['admin', 'owner', 'superadmin'].includes(currentRole);

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [actionBusyId, setActionBusyId] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');

  const [backfillBusy, setBackfillBusy] = useState(false);
  const [backfillResult, setBackfillResult] = useState({
    message: '',
    error: false,
  });

  const [createForm, setCreateForm] = useState(() => ({
    ...INITIAL_CREATE_FORM,
    password: generateTemporaryPassword(),
  }));
  const [showCreatePassword, setShowCreatePassword] = useState(false);
  const [createdCredential, setCreatedCredential] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [editBusy, setEditBusy] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState(INITIAL_EDIT_FORM);
  const [editTempPassword, setEditTempPassword] = useState('');
  const [showEditPassword, setShowEditPassword] = useState(false);
  const [tempPasswordBusy, setTempPasswordBusy] = useState(false);

  const filteredUsers = useMemo(() => {
    const query = stringValue(search).toLowerCase();
    if (!query) return users;

    return users.filter((user) =>
      [
        user?.name,
        user?.fullName,
        user?.email,
        user?.mobile,
        user?.username,
        user?.srId,
        user?.staffId,
        user?.employeeId,
        user?.role,
        user?.centre,
        user?.center,
        user?.status,
      ]
        .map((value) => stringValue(value).toLowerCase())
        .join(' ')
        .includes(query)
    );
  }, [users, search]);

  const loadUsers = async ({ preserveMessages = false } = {}) => {
    if (!preserveMessages) {
      setError('');
      setInfo('');
    }
    setLoading(true);

    try {
      const response = await requestWithFallback([
        {
          method: 'GET',
          url: '/api/users',
          options: { params: { page: 1, limit: 200 } },
        },
        { method: 'GET', url: '/api/admin/users' },
      ]);

      const list = normalizeUsers(response);
      setUsers(list);
      if (!list.length && !preserveMessages) setInfo('No users found yet.');
    } catch (loadError) {
      setUsers([]);
      setError(
        `Cannot load users right now. ${getErrorMessage(
          loadError,
          'User API unavailable.'
        )}`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateCreateField(field, value) {
    setError('');
    setInfo('');
    setCreateForm((current) => ({ ...current, [field]: value }));
  }

  function resetCreateForm() {
    setCreateForm({
      ...INITIAL_CREATE_FORM,
      password: generateTemporaryPassword(),
    });
    setShowCreatePassword(false);
    setCreatedCredential(null);
    setError('');
    setInfo('');
  }

  function validateCreateForm() {
    if (!canManageUsers) return 'Only CMFM Admin can create users.';
    if (!stringValue(createForm.name)) return 'Full name is required.';
    if (!stringValue(createForm.email) && !stringValue(createForm.mobile)) {
      return 'Provide at least one login identifier: Email or Mobile.';
    }
    if (!isValidEmail(createForm.email)) return 'Enter a valid email address.';
    if (!isValidMobile(createForm.mobile)) return 'Enter a valid mobile number.';
    if (!normalizeRole(createForm.role)) return 'Select a valid role.';
    if (String(createForm.password || '').length < 6) {
      return 'Temporary password must be at least 6 characters.';
    }
    if (String(createForm.password || '').length > 128) {
      return 'Temporary password cannot exceed 128 characters.';
    }
    return '';
  }

  async function createUser() {
    const validationMessage = validateCreateForm();
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setError('');
    setInfo('');

    const passwordUsed = String(createForm.password);
    const payload = {
      name: stringValue(createForm.name),
      email: stringValue(createForm.email).toLowerCase() || undefined,
      mobile:
        stringValue(createForm.mobile).replace(/[\s()-]/g, '') || undefined,
      username: stringValue(createForm.username) || undefined,
      srId: stringValue(createForm.srId) || undefined,
      role: normalizeRole(createForm.role),
      centre: normalizeCentre(createForm.centre) || undefined,
      password: passwordUsed,
      isActive: Boolean(createForm.isActive),
      mustChangePassword: Boolean(createForm.mustChangePassword),
    };

    const localConflict = findExistingCreateIdentity(
      users,
      payload.email,
      payload.mobile
    );
    if (localConflict) {
      const conflictIdentifier =
        localConflict.email || localConflict.mobile || localConflict.name || 'this user';
      setError(
        `An account already exists with this email or mobile (${conflictIdentifier}). Use Edit User to change details or set a temporary password.`
      );
      return;
    }

    setCreating(true);

    try {
      const response = await requestWithFallback([
        { method: 'POST', url: '/api/users/register', data: payload },
        { method: 'POST', url: '/api/admin/users', data: payload },
      ]);
      const responseData = payloadOf(response) || {};
      if (responseData?.claimedExisting === true) {
        throw new Error(
          'This identifier belongs to an existing user. Create User cannot modify an existing account; use Edit User instead.'
        );
      }

      const created =
        responseData?.user ||
        responseData?.data?.user ||
        responseData?.data ||
        null;

      setCreatedCredential({
        name: created?.name || payload.name,
        identifier:
          created?.email ||
          created?.mobile ||
          payload.email ||
          payload.mobile,
        password: passwordUsed,
      });

      setInfo(
        `User created: ${created?.name || payload.name} (${roleChipLabel(
          created?.role || payload.role
        )}).`
      );

      setCreateForm({
        ...INITIAL_CREATE_FORM,
        password: generateTemporaryPassword(),
      });
      setShowCreatePassword(false);
      await loadUsers({ preserveMessages: true });
    } catch (createError) {
      setError(getErrorMessage(createError, 'Create failed.'));
    } finally {
      setCreating(false);
    }
  }

  async function toggleActive(user) {
    if (!canManageUsers) {
      setError('Only CMFM Admin can activate or deactivate users.');
      return;
    }

    const userId = stringValue(user?._id || user?.id);
    if (!userId) return;

    const nextActive = !userIsActive(user);
    setActionBusyId(userId);
    setError('');
    setInfo('');

    try {
      await requestWithFallback([
        {
          method: 'PATCH',
          url: `/api/admin/users/${encodeURIComponent(userId)}`,
          data: { isActive: nextActive },
        },
        {
          method: 'PATCH',
          url: `/api/users/${encodeURIComponent(userId)}`,
          data: { isActive: nextActive },
        },
      ]);

      setInfo(`User ${nextActive ? 'activated' : 'deactivated'} successfully.`);
      await loadUsers({ preserveMessages: true });
    } catch (updateError) {
      setError(getErrorMessage(updateError, 'Update failed.'));
    } finally {
      setActionBusyId('');
    }
  }

  function openProfileEditor(userId) {
    const id = stringValue(userId);
    if (!id) return;
    navigate(tenantUrl(`/profile/${encodeURIComponent(id)}`));
  }

  function openEditUser(user) {
    if (!user) return;

    setEditUser(user);
    setEditForm({
      name: stringValue(user.name || user.fullName),
      email: stringValue(user.email),
      mobile: stringValue(user.mobile),
      username: stringValue(user.username),
      srId: stringValue(user.srId),
      staffId: stringValue(user.staffId),
      employeeId: stringValue(user.employeeId),
      role: normalizeRole(user.role),
      centre: normalizeCentre(user.centre || user.center),
      isActive: userIsActive(user),
    });
    setEditTempPassword('');
    setShowEditPassword(false);
    setEditOpen(true);
  }

  function closeEditUser() {
    if (editBusy || tempPasswordBusy) return;
    setEditOpen(false);
    setEditUser(null);
    setEditForm(INITIAL_EDIT_FORM);
    setEditTempPassword('');
    setShowEditPassword(false);
  }

  async function saveEditUser() {
    if (!editUser || editBusy) return;
    if (!canManageUsers) {
      setError('Only CMFM Admin can edit users.');
      return;
    }

    const userId = stringValue(editUser._id || editUser.id);
    if (!userId) return;
    if (!stringValue(editForm.name)) {
      setError('Name is required.');
      return;
    }
    if (!stringValue(editForm.email) && !stringValue(editForm.mobile)) {
      setError('Email or Mobile is required.');
      return;
    }
    if (!isValidEmail(editForm.email)) {
      setError('Enter a valid email address.');
      return;
    }
    if (!isValidMobile(editForm.mobile)) {
      setError('Enter a valid mobile number.');
      return;
    }

    setError('');
    setInfo('');
    setEditBusy(true);

    const payload = {
      name: stringValue(editForm.name),
      email: stringValue(editForm.email).toLowerCase() || undefined,
      mobile:
        stringValue(editForm.mobile).replace(/[\s()-]/g, '') || undefined,
      username: stringValue(editForm.username) || undefined,
      srId: stringValue(editForm.srId) || undefined,
      staffId: stringValue(editForm.staffId) || undefined,
      employeeId: stringValue(editForm.employeeId) || undefined,
      role: normalizeRole(editForm.role),
      centre: normalizeCentre(editForm.centre) || undefined,
      isActive: Boolean(editForm.isActive),
    };

    try {
      await requestWithFallback([
        {
          method: 'PATCH',
          url: `/api/admin/users/${encodeURIComponent(userId)}`,
          data: payload,
        },
        {
          method: 'PATCH',
          url: `/api/users/${encodeURIComponent(userId)}`,
          data: payload,
        },
      ]);

      setInfo('User details updated successfully.');
      await loadUsers({ preserveMessages: true });
      setEditOpen(false);
      setEditUser(null);
    } catch (updateError) {
      setError(getErrorMessage(updateError, 'Update failed.'));
    } finally {
      setEditBusy(false);
    }
  }

  async function setUserTemporaryPassword() {
    if (!editUser || tempPasswordBusy) return;
    if (!canManageUsers) {
      setError('Only CMFM Admin can reset user passwords.');
      return;
    }

    const userId = stringValue(editUser._id || editUser.id);
    const password = String(editTempPassword || '');
    if (!userId) return;
    if (password.length < 6 || password.length > 128) {
      setError('Temporary password must be 6 to 128 characters.');
      return;
    }

    setError('');
    setInfo('');
    setTempPasswordBusy(true);

    try {
      await requestWithFallback([
        {
          method: 'POST',
          url: `/api/admin/users/${encodeURIComponent(userId)}/password`,
          data: { password, mustChangePassword: true },
        },
        {
          method: 'POST',
          url: `/api/users/${encodeURIComponent(userId)}/password`,
          data: { password, mustChangePassword: true },
        },
      ]);

      setInfo('Temporary password set. The user must change it after login.');
      setEditTempPassword('');
      setShowEditPassword(false);
    } catch (passwordError) {
      setError(getErrorMessage(passwordError, 'Password update failed.'));
    } finally {
      setTempPasswordBusy(false);
    }
  }
  async function disableUserSoft() {
    if (!editUser || editBusy) return;
    if (!canManageUsers) {
      setError('Only CMFM Admin can disable users.');
      return;
    }

    const userId = stringValue(editUser._id || editUser.id);
    if (!userId) return;

    const confirmed = window.confirm(
      `Disable ${editUser.name || 'this user'}? The account should remain in records but lose login access.`
    );
    if (!confirmed) return;

    setError('');
    setInfo('');
    setEditBusy(true);

    try {
      await requestWithFallback([
        {
          method: 'DELETE',
          url: `/api/admin/users/${encodeURIComponent(userId)}`,
        },
        {
          method: 'DELETE',
          url: `/api/users/${encodeURIComponent(userId)}`,
        },
      ]);

      setInfo('User disabled successfully.');
      await loadUsers({ preserveMessages: true });
      setEditOpen(false);
      setEditUser(null);
    } catch (disableError) {
      setError(getErrorMessage(disableError, 'Disable failed.'));
    } finally {
      setEditBusy(false);
    }
  }

  async function handleBackfillProfiles() {
    if (backfillBusy) return;
    if (!canManageUsers) {
      setBackfillResult({
        message: 'Only CMFM Admin can run profile backfill.',
        error: true,
      });
      return;
    }

    setBackfillResult({ message: '', error: false });
    setBackfillBusy(true);

    try {
      const response = await http.post('/api/profiles/admin/backfill', {});
      const result = payloadOf(response) || {};
      const created = result?.created ?? result?.data?.created ?? 0;
      const skipped = result?.skipped ?? result?.data?.skipped ?? 0;
      const message =
        result?.message ||
        result?.data?.message ||
        `Backfill completed. Created: ${created}, Skipped: ${skipped}`;

      setBackfillResult({ message, error: false });
    } catch (backfillError) {
      setBackfillResult({
        message: getErrorMessage(backfillError, 'Backfill failed.'),
        error: true,
      });
    } finally {
      setBackfillBusy(false);
    }
  }

  async function copyText(value, successMessage) {
    try {
      await navigator.clipboard.writeText(String(value || ''));
      setInfo(successMessage);
    } catch {
      setError('Clipboard access is unavailable. Copy the value manually.');
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: 0.2 }}>
            User Management
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.8 }}>
            Create and manage tenant-scoped CMFM users, including Faculty and
            Admin accounts.
          </Typography>
        </Box>

        <Tooltip title="Refresh users">
          <span>
            <IconButton onClick={() => loadUsers()} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      {!canManageUsers && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This page is restricted to CMFM Admin. Your current role is{' '}
          <strong>{roleChipLabel(currentRole)}</strong>. User records may be
          visible, but account-changing actions are disabled.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {info && (
        <Alert severity="info" onClose={() => setInfo('')} sx={{ mb: 2 }}>
          {info}
        </Alert>
      )}

      {createdCredential && (
        <Alert
          severity="success"
          onClose={() => setCreatedCredential(null)}
          sx={{ mb: 2 }}
        >
          <Stack spacing={1}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              Account created. Share this temporary credential securely and
              only once.
            </Typography>
            <Typography variant="body2">
              {createdCredential.name} •{' '}
              {createdCredential.identifier || 'Identifier not returned'}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography
                component="code"
                variant="body2"
                sx={{ fontWeight: 800 }}
              >
                {createdCredential.password}
              </Typography>
              <Button
                size="small"
                startIcon={<ContentCopyIcon fontSize="small" />}
                onClick={() =>
                  copyText(
                    createdCredential.password,
                    'Temporary password copied to clipboard.'
                  )
                }
              >
                Copy password
              </Button>
            </Stack>
          </Stack>
        </Alert>
      )}

      <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Profiles for Existing Users
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              One-time action: create missing profiles for this tenant.
              Existing profiles are not modified.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            onClick={handleBackfillProfiles}
            disabled={backfillBusy || !canManageUsers}
            sx={{ borderRadius: 3, fontWeight: 800 }}
          >
            {backfillBusy ? 'Backfilling…' : 'Backfill Profiles'}
          </Button>
        </Stack>

        {backfillResult.message && (
          <Alert
            severity={backfillResult.error ? 'error' : 'success'}
            sx={{ mt: 2 }}
          >
            {backfillResult.message}
          </Alert>
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: 2.5, mb: 3, borderRadius: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, mb: 1 }}>
          Create New User
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.75, mb: 2 }}>
          Citizen accounts are created through Citizen Registration, not this
          staff administration page.
        </Typography>
        <Divider sx={{ mb: 2, opacity: 0.25 }} />

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(3, minmax(0, 1fr))',
            },
            gap: 2,
            alignItems: 'center',
          }}
        >
          <TextField
            label="Full Name"
            value={createForm.name}
            onChange={(event) =>
              updateCreateField('name', event.target.value)
            }
            fullWidth
            required
            disabled={!canManageUsers || creating}
          />

          <TextField
            label="Email (optional)"
            value={createForm.email}
            onChange={(event) =>
              updateCreateField('email', event.target.value)
            }
            fullWidth
            disabled={!canManageUsers || creating}
            autoComplete="off"
          />

          <TextField
            label="Mobile (optional)"
            value={createForm.mobile}
            onChange={(event) =>
              updateCreateField('mobile', event.target.value)
            }
            fullWidth
            disabled={!canManageUsers || creating}
            autoComplete="off"
          />

          <TextField
            label="Username (optional)"
            value={createForm.username}
            onChange={(event) =>
              updateCreateField('username', event.target.value)
            }
            fullWidth
            disabled={!canManageUsers || creating}
            autoComplete="off"
          />

          <TextField
            label="SR / Staff Identifier (optional)"
            value={createForm.srId}
            onChange={(event) =>
              updateCreateField('srId', event.target.value)
            }
            fullWidth
            disabled={!canManageUsers || creating}
            autoComplete="off"
          />

          <FormControl fullWidth disabled={!canManageUsers || creating}>
            <InputLabel>Role</InputLabel>
            <Select
              value={createForm.role}
              label="Role"
              onChange={(event) =>
                updateCreateField('role', event.target.value)
              }
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!canManageUsers || creating}>
            <InputLabel>Centre (optional)</InputLabel>
            <Select
              value={createForm.centre}
              label="Centre (optional)"
              onChange={(event) =>
                updateCreateField('centre', event.target.value)
              }
            >
              <MenuItem value="">
                <em>Not assigned</em>
              </MenuItem>
              {CENTRES.map((centre) => (
                <MenuItem key={centre} value={centre}>
                  {centre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Temporary Password"
            value={createForm.password}
            onChange={(event) =>
              updateCreateField('password', event.target.value)
            }
            type={showCreatePassword ? 'text' : 'password'}
            fullWidth
            disabled={!canManageUsers || creating}
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      showCreatePassword ? 'Hide password' : 'Show password'
                    }
                  >
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() =>
                        setShowCreatePassword((current) => !current)
                      }
                      disabled={!canManageUsers || creating}
                    >
                      {showCreatePassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              checked={createForm.isActive}
              onChange={(event) =>
                updateCreateField('isActive', event.target.checked)
              }
              disabled={!canManageUsers || creating}
            />
            <Typography variant="body2">
              {createForm.isActive ? 'Account active' : 'Account inactive'}
            </Typography>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Switch
              checked={createForm.mustChangePassword}
              onChange={(event) =>
                updateCreateField(
                  'mustChangePassword',
                  event.target.checked
                )
              }
              disabled={!canManageUsers || creating}
            />
            <Typography variant="body2">
              Require password change at first login
            </Typography>
          </Stack>
        </Box>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <Button
            variant="contained"
            onClick={createUser}
            disabled={creating || !canManageUsers}
          >
            {creating ? 'Creating…' : 'Create User'}
          </Button>

          <Button
            variant="outlined"
            onClick={() =>
              updateCreateField('password', generateTemporaryPassword())
            }
            disabled={creating || !canManageUsers}
          >
            Generate Password
          </Button>

          <Button
            variant="outlined"
            onClick={resetCreateForm}
            disabled={creating || !canManageUsers}
          >
            Clear
          </Button>
        </Stack>
      </Paper>

      <Paper elevation={2} sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Existing Users ({filteredUsers.length} of {users.length})
          </Typography>

          <TextField
            label="Search name, identifier, role or centre"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            sx={{ width: { xs: '100%', md: 380 } }}
          />
        </Stack>

        <Divider sx={{ my: 2, opacity: 0.25 }} />

        {loading ? (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : filteredUsers.length === 0 ? (
          <Alert severity="info">No matching users found.</Alert>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <Box
              sx={{
                minWidth: 1160,
                display: 'grid',
                gridTemplateColumns:
                  '1.4fr 1fr 1.4fr 1fr 1fr 0.8fr 1.7fr',
                gap: 1,
                alignItems: 'center',
                fontWeight: 800,
                opacity: 0.85,
                mb: 1,
              }}
            >
              <Box>Name / Identifier</Box>
              <Box>Role</Box>
              <Box>Email</Box>
              <Box>Mobile</Box>
              <Box>Centre</Box>
              <Box>Status</Box>
              <Box>Actions</Box>
            </Box>

            <Divider sx={{ mb: 1, opacity: 0.2 }} />

            {filteredUsers.map((user, index) => {
              const userId = stringValue(user?._id || user?.id);
              const active = userIsActive(user);
              const identifier =
                stringValue(user?.username) ||
                stringValue(user?.srId) ||
                stringValue(user?.staffId) ||
                stringValue(user?.employeeId);
              const rowBusy = actionBusyId === userId;

              return (
                <Box
                  key={stableUserKey(user, index)}
                  sx={{
                    minWidth: 1160,
                    display: 'grid',
                    gridTemplateColumns:
                      '1.4fr 1fr 1.4fr 1fr 1fr 0.8fr 1.7fr',
                    gap: 1,
                    alignItems: 'center',
                    py: 1.1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 700 }}
                      noWrap
                    >
                      {user?.name || user?.fullName || '—'}
                    </Typography>
                    {identifier && (
                      <Typography
                        variant="caption"
                        sx={{ opacity: 0.7 }}
                        noWrap
                      >
                        {identifier}
                      </Typography>
                    )}
                  </Box>

                  <Box>
                    <Chip
                      size="small"
                      label={roleChipLabel(user?.role)}
                      color={roleChipColor(user?.role)}
                      variant="outlined"
                    />
                  </Box>

                  <Box sx={{ opacity: 0.9, overflowWrap: 'anywhere' }}>
                    {user?.email || '—'}
                  </Box>
                  <Box sx={{ opacity: 0.9 }}>
                    {user?.mobile || '—'}
                  </Box>
                  <Box sx={{ opacity: 0.9 }}>
                    {normalizeCentre(user?.centre || user?.center) || '—'}
                  </Box>

                  <Box>
                    <Chip
                      size="small"
                      label={active ? 'Active' : 'Inactive'}
                      color={active ? 'success' : 'default'}
                      variant="filled"
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openProfileEditor(userId)}
                      disabled={!userId}
                    >
                      Profile
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => openEditUser(user)}
                      disabled={!canManageUsers || !userId}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => toggleActive(user)}
                      disabled={!canManageUsers || !userId || rowBusy}
                    >
                      {rowBusy
                        ? 'Updating…'
                        : active
                          ? 'Deactivate'
                          : 'Activate'}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      <Dialog
        open={editOpen && Boolean(editUser)}
        onClose={closeEditUser}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Edit User
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.75 }}>
                {editUser?.name || 'User'} •{' '}
                {roleChipLabel(editUser?.role)}
              </Typography>
            </Box>
            <IconButton
              onClick={closeEditUser}
              size="small"
              aria-label="Close"
              disabled={editBusy || tempPasswordBusy}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            <TextField
              label="Name"
              value={editForm.name}
              onChange={(event) =>
                setEditForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
              fullWidth
              required
              disabled={editBusy}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Email"
                value={editForm.email}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                fullWidth
                disabled={editBusy}
              />
              <TextField
                label="Mobile"
                value={editForm.mobile}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    mobile: event.target.value,
                  }))
                }
                fullWidth
                disabled={editBusy}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Username"
                value={editForm.username}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    username: event.target.value,
                  }))
                }
                fullWidth
                disabled={editBusy}
              />
              <TextField
                label="SR ID"
                value={editForm.srId}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    srId: event.target.value,
                  }))
                }
                fullWidth
                disabled={editBusy}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Staff ID"
                value={editForm.staffId}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    staffId: event.target.value,
                  }))
                }
                fullWidth
                disabled={editBusy}
              />
              <TextField
                label="Employee ID"
                value={editForm.employeeId}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    employeeId: event.target.value,
                  }))
                }
                fullWidth
                disabled={editBusy}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControl fullWidth disabled={editBusy}>
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  label="Role"
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      role: event.target.value,
                    }))
                  }
                >
                  {ROLE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth disabled={editBusy}>
                <InputLabel>Centre</InputLabel>
                <Select
                  value={editForm.centre}
                  label="Centre"
                  onChange={(event) =>
                    setEditForm((current) => ({
                      ...current,
                      centre: event.target.value,
                    }))
                  }
                >
                  <MenuItem value="">
                    <em>Not assigned</em>
                  </MenuItem>
                  {CENTRES.map((centre) => (
                    <MenuItem key={centre} value={centre}>
                      {centre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>

            <Stack direction="row" spacing={1} alignItems="center">
              <Switch
                checked={editForm.isActive}
                onChange={(event) =>
                  setEditForm((current) => ({
                    ...current,
                    isActive: event.target.checked,
                  }))
                }
                disabled={editBusy}
              />
              <Typography>
                {editForm.isActive ? 'Account active' : 'Account inactive'}
              </Typography>
            </Stack>

            <Divider />

            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              Set Temporary Password
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.75 }}>
              The user will be required to change this password after the next
              login.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems="center"
            >
              <TextField
                label="Temporary password"
                value={editTempPassword}
                onChange={(event) =>
                  setEditTempPassword(event.target.value)
                }
                type={showEditPassword ? 'text' : 'password'}
                fullWidth
                disabled={tempPasswordBusy || editBusy}
                autoComplete="new-password"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() =>
                          setShowEditPassword((current) => !current)
                        }
                        disabled={tempPasswordBusy || editBusy}
                      >
                        {showEditPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                variant="outlined"
                onClick={() =>
                  setEditTempPassword(generateTemporaryPassword())
                }
                disabled={tempPasswordBusy || editBusy}
                sx={{ minWidth: 130, height: 56 }}
              >
                Generate
              </Button>

              <Button
                variant="outlined"
                onClick={setUserTemporaryPassword}
                disabled={
                  tempPasswordBusy ||
                  editBusy ||
                  editTempPassword.length < 6 ||
                  !canManageUsers
                }
                sx={{ minWidth: 190, height: 56 }}
              >
                {tempPasswordBusy
                  ? 'Setting…'
                  : 'Set temporary password'}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button
            onClick={closeEditUser}
            disabled={editBusy || tempPasswordBusy}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="outlined"
            onClick={disableUserSoft}
            disabled={editBusy || tempPasswordBusy || !canManageUsers}
          >
            Disable User
          </Button>
          <Button
            variant="contained"
            onClick={saveEditUser}
            disabled={editBusy || tempPasswordBusy || !canManageUsers}
          >
            {editBusy ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
